import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import TemplateThumb from "../components/TemplateThumb";
import {
  TEMPLATE_GALLERY,
  TEMPLATE_STYLES,
  TEMPLATE_COMMUNITIES,
} from "../lib/templates";

export default function Templates() {
  const [style, setStyle] = useState("all");
  const [community, setCommunity] = useState("All");
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    return TEMPLATE_GALLERY.filter((t) => {
      if (style !== "all" && t.template !== style) return false;
      if (community !== "All" && t.community !== community) return false;
      return true;
    });
  }, [style, community]);

  const openBuilder = (variant) => {
    // Pass template + religion via query params so the builder can pre-fill.
    const params = new URLSearchParams({
      template: variant.template,
      religion: variant.community,
      variant: variant.id,
    });
    navigate(`/biodata/build?${params.toString()}`);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "InstaVivah biodata templates gallery",
    description:
      "Browse 50+ marriage biodata templates for Hindu, Muslim, Christian, Sikh, Jain, Buddhist, Parsi, Marathi, Tamil, Gujarati and more Indian communities.",
    numberOfItems: TEMPLATE_GALLERY.length,
  };

  return (
    <div>
      <SEO
        title="Biodata Templates Gallery — 50+ Free Marriage Biodata Designs"
        path="/templates"
        description="Browse over 50 free marriage biodata templates by InstaVivah. Choose classic, modern, and royal designs tailored for Hindu, Muslim, Christian, Sikh, Jain, Buddhist, Marathi, Tamil, Gujarati and more communities."
        keywords="biodata templates, marriage biodata design, hindu biodata, muslim biodata, christian biodata, buddhist biodata, sikh biodata, marathi biodata, tamil biodata, gujarati biodata, wedding biodata template"
        jsonLd={jsonLd}
      />
      <Navbar />

      <header className="sb-page-hero text-center">
        <div className="container">
          <div className="sb-kicker mb-2">Templates gallery</div>
          <h1>50+ beautiful biodata templates.</h1>
          <p>
            Classic, Modern, and Royal designs — thoughtfully tuned for every
            Indian community. Pick one, click <em>Use this template</em>, and
            start filling in your details right away.
          </p>
        </div>
      </header>

      <main className="container py-4" data-testid="templates-page">
        {/* Filter bar */}
        <div className="iv-template-filters" data-testid="template-filters">
          <div className="iv-template-filter-group">
            <span className="iv-template-filter-label">Style</span>
            <div className="d-flex flex-wrap gap-2">
              {TEMPLATE_STYLES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={
                    "iv-pill" + (style === s.id ? " is-active" : "")
                  }
                  onClick={() => setStyle(s.id)}
                  data-testid={`filter-style-${s.id}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="iv-template-filter-group">
            <span className="iv-template-filter-label">Community</span>
            <div className="iv-template-community-scroll">
              {TEMPLATE_COMMUNITIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={
                    "iv-pill" + (community === c ? " is-active" : "")
                  }
                  onClick={() => setCommunity(c)}
                  data-testid={`filter-community-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="text-muted small mt-3 mb-4">
          Showing <strong>{filtered.length}</strong> of{" "}
          {TEMPLATE_GALLERY.length} templates
        </p>

        <div className="iv-template-grid" data-testid="templates-grid">
          {filtered.map((t) => (
            <article
              key={t.id}
              className="iv-template-card"
              data-testid={`template-card-${t.id}`}
            >
              {t.popular && (
                <span className="iv-template-badge iv-template-badge-red">
                  Most Popular
                </span>
              )}
              {t.isNew && (
                <span className="iv-template-badge iv-template-badge-green">
                  New — Buddhist
                </span>
              )}
              <div
                className="iv-template-preview"
                style={{ background: t.tint }}
              >
                <TemplateThumb variant={t} />
              </div>
              <div className="iv-template-body">
                <div className="iv-template-tags">
                  <span className="iv-template-tag">{t.style}</span>
                  <span className="iv-template-tag">{t.community}</span>
                </div>
                <h3 className="iv-template-title">{t.title}</h3>
                <p className="iv-template-sub">{t.subtitle}</p>
                <button
                  type="button"
                  className="iv-btn iv-btn-primary iv-btn-sm w-100"
                  onClick={() => openBuilder(t)}
                  data-testid={`template-use-${t.id}`}
                >
                  Use this template
                  <span className="iv-arrow" aria-hidden="true">
                    →
                  </span>
                </button>
              </div>
            </article>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="sb-card text-center py-5">
            <div className="sb-kicker mb-2">Nothing here</div>
            <h3 className="h5">No templates match those filters.</h3>
            <p className="text-muted">Try a different combination.</p>
          </div>
        )}

        <section
          className="sb-card mt-5 d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3"
          style={{
            background:
              "linear-gradient(135deg, rgba(229,37,74,0.05), rgba(243,156,31,0.08))",
            border: "1px dashed var(--iv-red)",
          }}
        >
          <div>
            <div className="sb-kicker mb-1">Not sure which to pick?</div>
            <h3 className="h4 mb-1">Start with our most popular template.</h3>
            <p className="mb-0 text-muted">
              You can switch templates anytime while building — no data lost.
            </p>
          </div>
          <Link
            to="/biodata/build"
            className="iv-btn iv-btn-primary"
            data-testid="cta-start-any"
          >
            Start Building Free
            <span className="iv-arrow" aria-hidden="true">
              →
            </span>
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
